import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MdMic, MdStop, MdPlayArrow, MdPause, MdDelete, MdSend } from 'react-icons/md';
import deltaService from '../../services/deltaService';
import { Empty } from './DeltaUI';
import ChangeSummary from './ChangeSummary';

// The review trail on a version: what was said, and a way to say something.
//
// A comment carries text, a voice memo, or both. Recording uses the browser's own
// MediaRecorder — no library, no service, nothing leaves the page except the finished blob,
// which is uploaded to the same private vault as every other document.
//
// PLAYBACK IS DEFERRED ON PURPOSE. Each memo is fetched only when someone presses play, via
// a short-lived presigned URL. Requesting URLs for every memo on page load would mint a
// pile of live links to confidential audio that nobody asked to hear.
//
// Nothing here can be edited or deleted. A review trail that can be rewritten afterwards is
// not a trail, and the API has no route for it either.

// The recorder's own mimeType decides the extension: Chrome gives webm/opus, Safari mp4/aac.
// Guessing instead would produce a file the vault refuses on extension.
const EXT = [
  ['audio/webm', 'webm'],
  ['audio/ogg', 'ogg'],
  ['audio/mp4', 'm4a'],
  ['audio/mpeg', 'mp3'],
];

const extFor = (mime) => (EXT.find(([m]) => (mime || '').startsWith(m)) || [null, 'webm'])[1];

const mmss = (s) => {
  if (s == null) return '';
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
};

export default function ReviewTrail({ version, versions, onChanged }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (!version?.id) { setLoading(false); return; }
    setLoading(true);
    deltaService.getVersionComments(version.id)
      .then(setComments).catch(() => setComments([])).finally(() => setLoading(false));
  }, [version]);

  useEffect(() => { load(); }, [load]);

  // Do NOT bail out when nothing is in force. A draft exists to be commented on and
  // submitted — refusing to show the trail until something is approved hid the Submit
  // button from the person who had just uploaded the draft.
  if (!version?.id) {
    return (
      <section className="mt-8">
        <h2 className="font-serif text-[17px] text-gray-900 dark:text-gray-50">Review trail</h2>
        <Empty>Nothing has been drafted yet, so there is nothing to comment on.</Empty>
        <InFlight versions={versions} onChanged={onChanged} />
      </section>
    );
  }

  return (
    <section className="mt-8">
      <h2 className="font-serif text-[17px] text-gray-900 dark:text-gray-50">Review trail</h2>
      <p className="mt-0.5 text-[11.5px] text-gray-500 dark:text-gray-400">
        On version {version.label} · {version.stage_display}. Comments cannot be edited or
        removed once left.
      </p>

      <div className="mt-3 space-y-2">
        {loading && <Empty>Loading…</Empty>}
        {!loading && !comments.length && <Empty>No comments yet.</Empty>}
        {comments.map((c) => <CommentRow key={c.id} comment={c} />)}
      </div>

      <Composer versionId={version.id} onAdded={load} />

      <InFlight versions={versions} onChanged={onChanged} />
    </section>
  );
}

// Versions still moving through the pipeline. Only those this user is allowed to see are
// returned by the server, and each carries exactly the transitions they may take — the
// buttons are the API's answer, not the client's guess, so a screen can never offer a step
// the server would refuse.
function InFlight({ versions, onChanged }) {
  const moving = (versions || []).filter(
    (v) => !['approved', 'superseded', 'withdrawn'].includes(v.stage));
  if (!moving.length) return null;
  return (
    <section className="mt-6">
      <h3 className="text-[12px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
        In progress
      </h3>
      <div className="mt-2 space-y-2">
        {moving.map((v) => <VersionRow key={v.id} version={v} onChanged={onChanged} />)}
      </div>
    </section>
  );
}

function VersionRow({ version, onChanged }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const act = async (action) => {
    setBusy(true); setError('');
    try {
      await deltaService.versionAction(version.id, action, note);
      setNote('');
      onChanged?.();
    } catch (err) {
      // Authored server-side: it names the authority that owns the step.
      setError(err?.response?.data?.detail || 'That step could not be taken.');
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-800">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] font-medium text-gray-900 dark:text-gray-100">
          Version {version.label}
        </span>
        <span className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-400">
          {version.stage_display}
        </span>
      </div>

      {version.actions?.length ? (
        <>
          <input value={note} onChange={(e) => setNote(e.target.value)}
                 placeholder="Note (required when returning for revision)"
                 className="mt-2 w-full rounded-md border border-gray-200 bg-transparent px-3 py-1.5 text-[12px] text-gray-900 outline-none focus:border-brand-gold dark:border-gray-700 dark:text-gray-100" />
          <div className="mt-2 flex flex-wrap gap-2">
            {version.actions.map((a) => (
              <button key={a.action} type="button" disabled={busy} onClick={() => act(a.action)}
                      className={`rounded-md px-3 py-2 text-[12px] font-medium disabled:opacity-40 ${
                        a.action === 'return'
                          ? 'border border-gray-200 text-gray-600 dark:border-gray-700 dark:text-gray-300'
                          : 'bg-brand-gold text-white'}`}>
                {a.label}
              </button>
            ))}
          </div>
        </>
      ) : (
        <p className="mt-1.5 text-[11.5px] text-gray-500 dark:text-gray-400">
          Waiting on someone else. Nothing here is yours to move.
        </p>
      )}

      {/* A reviewer decides on the CHANGES, not on the document in the abstract. */}
      <ChangeSummary summary={version.change_summary} compact />

      {error && (
        <div className="mt-2 rounded border-l-2 border-rose-500 bg-rose-50 px-3 py-2 text-[12px] text-rose-900 dark:bg-rose-500/10 dark:text-rose-200">
          {error}
        </div>
      )}
    </div>
  );
}

function CommentRow({ comment }) {
  return (
    <article className="rounded-lg border border-gray-200 p-3 dark:border-gray-800">
      <div className="flex flex-wrap items-baseline gap-x-2 text-[11px] text-gray-500 dark:text-gray-400">
        <span className="font-medium text-gray-800 dark:text-gray-200">{comment.author_name}</span>
        {comment.actor_role && <span className="uppercase tracking-wider">{comment.actor_role}</span>}
        <span>{new Date(comment.at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}</span>
      </div>
      {comment.body && (
        <p className="mt-1.5 whitespace-pre-wrap text-[13px] leading-relaxed text-gray-800 dark:text-gray-200">
          {comment.body}
        </p>
      )}
      {comment.has_audio && (
        <AudioPlayer documentId={comment.audio_document} seconds={comment.audio_seconds} />
      )}
    </article>
  );
}

// Fetches the presigned URL on first press, then plays. `download` uses the same URL, so
// the file is reachable without a second round trip.
function AudioPlayer({ documentId, seconds }) {
  const audioRef = useRef(null);
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);

  const play = async () => {
    if (url) {
      if (playing) { audioRef.current?.pause(); } else { audioRef.current?.play(); }
      return;
    }
    setBusy(true); setFailed(false);
    try {
      const r = await deltaService.downloadDocument(documentId);
      const link = r?.url || r?.download_url;
      if (!link) throw new Error('no url');
      setUrl(link);
      // The element does not exist with a src until this render lands.
      setTimeout(() => audioRef.current?.play(), 0);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 flex items-center gap-2">
      <button type="button" onClick={play} disabled={busy}
              className="flex items-center gap-1 rounded-full border border-gray-200 px-2.5 py-1 text-[12px] text-gray-700 transition-colors hover:border-brand-gold hover:text-brand-gold disabled:opacity-40 dark:border-gray-700 dark:text-gray-200">
        {playing ? <MdPause size={15} /> : <MdPlayArrow size={15} />}
        {busy ? 'Loading…' : playing ? 'Pause' : 'Play'}
      </button>
      <span className="text-[11px] tabular-nums text-gray-400">{mmss(seconds)}</span>
      {url && (
        <a href={url} download className="text-[11px] text-gray-400 underline hover:text-brand-gold">
          Download
        </a>
      )}
      {failed && <span className="text-[11px] text-rose-600 dark:text-rose-400">Could not load.</span>}
      {url && (
        <audio ref={audioRef} src={url} preload="none"
               onPlay={() => setPlaying(true)}
               onPause={() => setPlaying(false)}
               onEnded={() => setPlaying(false)} />
      )}
    </div>
  );
}

function Composer({ versionId, onAdded }) {
  const [body, setBody] = useState('');
  const [blob, setBlob] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const startedRef = useRef(0);
  const tickRef = useRef(null);

  const stopTracks = () => {
    recorderRef.current?.stream?.getTracks?.().forEach((t) => t.stop());
  };

  // The microphone is released the moment recording ends, and on unmount. A page that
  // quietly holds an open mic is not acceptable in a room where this gets used.
  useEffect(() => () => { clearInterval(tickRef.current); stopTracks(); }, []);

  const startRecording = async () => {
    setError('');
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('This browser cannot record audio. Type a comment instead.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const type = rec.mimeType || 'audio/webm';
        const b = new Blob(chunksRef.current, { type });
        b.name = `memo.${extFor(type)}`;
        setBlob(b);
        stopTracks();
      };
      recorderRef.current = rec;
      startedRef.current = Date.now();
      setSeconds(0);
      tickRef.current = setInterval(
        () => setSeconds(Math.round((Date.now() - startedRef.current) / 1000)), 250);
      rec.start();
      setRecording(true);
    } catch {
      setError('Microphone access was refused.');
    }
  };

  const stopRecording = () => {
    clearInterval(tickRef.current);
    recorderRef.current?.stop();
    setRecording(false);
  };

  const submit = async () => {
    setBusy(true); setError('');
    try {
      // The blob carries `name` but is not a File; the vault validates on extension, so it
      // has to arrive with one.
      const audio = blob ? new File([blob], blob.name, { type: blob.type }) : null;
      await deltaService.addVersionComment(versionId, { body: body.trim(), audio, seconds: blob ? seconds : null });
      setBody(''); setBlob(null); setSeconds(0);
      onAdded();
    } catch (err) {
      setError(err?.response?.data?.detail || 'The comment could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  const empty = !body.trim() && !blob;

  return (
    <div className="mt-4 rounded-lg border border-dashed border-gray-300 p-3 dark:border-gray-700">
      <textarea
        value={body} onChange={(e) => setBody(e.target.value)} rows={3}
        placeholder="Leave a comment, or record a voice memo."
        className="w-full resize-y rounded-md border border-gray-200 bg-transparent px-3 py-2 text-[13px] text-gray-900 outline-none focus:border-brand-gold dark:border-gray-700 dark:text-gray-100"
      />

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {!recording ? (
          <button type="button" onClick={startRecording} disabled={busy || !!blob}
                  className="flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-2 text-[12px] text-gray-700 transition-colors hover:border-brand-gold hover:text-brand-gold disabled:opacity-40 dark:border-gray-700 dark:text-gray-200">
            <MdMic size={16} /> Record
          </button>
        ) : (
          <button type="button" onClick={stopRecording}
                  className="flex items-center gap-1.5 rounded-md bg-rose-600 px-3 py-2 text-[12px] font-medium text-white">
            <MdStop size={16} /> Stop · {mmss(seconds)}
          </button>
        )}

        {blob && !recording && (
          <>
            <span className="text-[12px] text-gray-600 dark:text-gray-300">
              Memo recorded · {mmss(seconds)}
            </span>
            <button type="button" onClick={() => { setBlob(null); setSeconds(0); }}
                    className="flex items-center gap-1 text-[12px] text-gray-400 hover:text-rose-600">
              <MdDelete size={14} /> Discard
            </button>
          </>
        )}

        <span className="flex-1" />

        <button type="button" onClick={submit} disabled={busy || empty || recording}
                className="flex items-center gap-1.5 rounded-md bg-brand-gold px-3 py-2 text-[12px] font-medium text-white disabled:opacity-40">
          <MdSend size={15} /> {busy ? 'Saving…' : 'Add to trail'}
        </button>
      </div>

      {blob && !recording && (
        <audio className="mt-2 w-full" controls src={URL.createObjectURL(blob)} />
      )}

      {error && (
        <div className="mt-2 rounded border-l-2 border-rose-500 bg-rose-50 px-3 py-2 text-[12px] text-rose-900 dark:bg-rose-500/10 dark:text-rose-200">
          {error}
        </div>
      )}
    </div>
  );
}
